import { useLazyQuery, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  Center,
  createStyles,
  Group,
  LoadingOverlay,
  Menu,
  ScrollArea,
  Select,
  SimpleGrid,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons";
import { INVENTORY_REPORT } from "apollo/queries";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useEffect, useState } from "react";
import { formatLocalDate, formatNumber } from "utiles/url";
import * as XLSX from "xlsx";
import WarehouseFilter from "./warehouse";

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
  },
  control: {
    width: "100%",
    padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },
  icon: {
    width: 15,
    height: 21,
    borderRadius: 21,
  },
  thh: {
    color: "#666666",
    fontFamily: "'__Inter_aaf875','__Inter_Fallback_aaf875'",
    fontSize: "10px",
    textTransform: "uppercase",
    fontWeight: "bold",
  },
}));

function Th({ children, sortable, sorted, reversed, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <th className={classes.th}>
      <UnstyledButton
        onClick={sortable ? onSort : null}
        className={classes.control}
      >
        <Group position="apart" spacing={5}>
          <Text weight={500} size="sm">
            {children}
          </Text>
          {sortable && (
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          )}
        </Group>
      </UnstyledButton>
    </th>
  );
}

const InventoryReport = () => {
  const [size, setSize] = useState("50");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [business, setBusiness] = useState(null);
  const [warehouse, setSetWarehouse] = useState(null);
  const [timeRange, setTimeRange] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [showCustomDates, setShowCustomDates] = useState(false);
  const { loading, data, error, refetch } = useQuery(INVENTORY_REPORT, {
    variables: {
      first: parseInt(size),
      page: activePage,
      period: timeRange || undefined,
      start_date: selectedStartDate
        ? formatLocalDate(selectedStartDate)
        : undefined,
      end_date: selectedEndDate ? formatLocalDate(selectedEndDate) : undefined,
      warehouse_ids: warehouse ? [warehouse] : undefined,
      orderBy: undefined,
    },
    skip: !size || !activePage,
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to get inventory report",
      });
    },
  });

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "name",
      sortable: false,
      searchable: false,
      width: "14%",
      render: (rowData) => {
        const MAX_LENGTH = 18;
        const shortName =
          rowData.name.length > MAX_LENGTH
            ? rowData.name.slice(0, MAX_LENGTH) + "..."
            : rowData.name;

        return <span title={rowData.name}>{shortName}</span>;
      },
    },
    {
      label: "Category",
      key: "category",
      sortable: false,
      searchable: false,
      render: (rowData) => (
        <span style={{ width: "20px" }}>{rowData.category?.name || "N/A"}</span>
      ),
    },
    {
      label: "Price",
      key: "price",
      sortable: false,
      searchable: false,
      width: "8%",

      render: (rowData) => <span>{formatNumber(rowData.unit_price)}</span>,
    },
    {
      label: "Sold Quantity",
      key: "sold_quantity",
      sortable: false,
      searchable: false,
      width: "8%",

      render: (rowData) => <span>{rowData.sold_quantity}</span>,
    },
    {
      label: "Remaining Quantity",
      key: "remaining_quantity",
      sortable: false,
      searchable: false,
      width: "8%",

      render: (rowData) => <span>{rowData.total_stock_level}</span>,
    },
    {
      label: "Sold Items Total Price",
      key: "sold_items_total_price",
      sortable: false,
      searchable: false,
      width: "8%",
      render: (rowData) => (
        <span>{formatNumber(rowData.sold_items_total_price)}</span>
      ),
    },
    {
      label: "Remaining Price",
      key: "remaining_items_total_price",
      sortable: false,
      searchable: false,
      width: "8%",

      render: (rowData) => (
        <span>{formatNumber(rowData.remaining_items_total_price)}</span>
      ),
    },
  ];

  useEffect(() => {
    refetch();
  }, []);

  const [fetchAllInventory] = useLazyQuery(INVENTORY_REPORT);

  const handleExportDataFetch = async () => {
    const { data: allData } = await fetchAllInventory({
      variables: {
        warehouse_ids: warehouse ? [warehouse] : undefined,
        period: timeRange || undefined,
        start_date: selectedStartDate
          ? formatLocalDate(selectedStartDate)
          : undefined,
        end_date: selectedEndDate
          ? formatLocalDate(selectedEndDate)
          : undefined,
        first: null,
        page: 1,
      },
    });
    return allData?.getProductsWithStockLevels;
  };

  useEffect(() => {
    if (data) {
      setTotal(data.getProductsWithStockLevels.paginatorInfo.lastPage);
    }
  }, [data, size]);

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  const handleReset = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setBusiness(null);
    setTimeRange(null);
    setSetWarehouse(null);
    setShowCustomDates(false);
    refetch();
  };

  const handleChange = (page) => {
    setActivePage(page);
  };
  const exportHeaders = headerData.map((h) => h.label);
  const exportToPDF = async () => {
    const reportData = await handleExportDataFetch();
    const rows = reportData?.data || [];

    const doc = new jsPDF("landscape");
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Inventory Report", pageWidth / 2, 20, { align: "center" });

    let subtitle = "Date: ";
    if (selectedStartDate && selectedEndDate) {
      subtitle += `${formatLocalDate(selectedStartDate)} - ${formatLocalDate(selectedEndDate)}`;
    } else if (timeRange && timeRange !== "CUSTOM") {
      subtitle += timeRange;
    } else {
      subtitle += "All Time";
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(subtitle, pageWidth / 2, 28, { align: "center" });

    const bodyData = rows.map((row, rowIndex) => {
      const idx = rowIndex + 1;
      return [
        idx,
        row.name,
        row.category?.name || "N/A",
        formatNumber(row.unit_price),
        row.sold_quantity,
        row.total_stock_level,
        formatNumber(row.sold_items_total_price),
        formatNumber(row.remaining_items_total_price),
      ];
    });

    const totalSold = formatNumber(reportData?.total_sold_amount || 0);
    const totalRemaining = formatNumber(
      reportData?.total_remaining_balance || 0,
    );

    doc.autoTable({
      startY: 35,
      head: [exportHeaders],
      body: bodyData,
      theme: "grid",
      headStyles: {
        fillColor: [27, 145, 223],
        textColor: [255, 255, 255],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [0, 0, 0],
      },
      styles: {
        fontSize: 8,
      },
      columnStyles: {
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
      },
    });
    const finalY = doc.lastAutoTable?.finalY || 10;
    doc.text(`Total Sold Price: ${totalSold}`, pageWidth - 20, finalY + 10, {
      align: "right",
    });
    doc.text(
      `Total Remaining Price: ${totalRemaining}`,
      pageWidth - 20,
      finalY + 20,
      { align: "right" },
    );
    doc.save(`inventory_report_${new Date().toLocaleDateString()}.pdf`);
  };

  const exportToExcel = async () => {
    const reportData = await handleExportDataFetch();
    const rows = reportData?.data || [];

    const bodyData = rows.map((row, rowIndex) => {
      const idx = rowIndex + 1;
      return {
        "#": idx,
        Name: row.name,
        Category: row.category?.name || "N/A",
        Price: row.unit_price,
        "Sold Quantity": row.sold_quantity,
        "Remaining Quantity": row.total_stock_level,
        "Sold Items Total Price": row.sold_items_total_price,
        "Remaining Price": row.remaining_items_total_price,
      };
    });

    const totalSold = reportData?.total_sold_amount || 0;
    const totalRemaining = reportData?.total_remaining_balance || 0;
    bodyData.push({
      "#": "",
      Name: "Total Sold Price",
      Category: "",
      Price: "",
      "Sold Quantity": "",
      "Remaining Quantity": "",
      "Sold Items Total Price": totalSold,
      "Remaining Price": "",
    });
    bodyData.push({
      "#": "",
      Name: "Total Remaining Price",
      Category: "",
      Price: "",
      "Sold Quantity": "",
      "Remaining Quantity": "",
      "Sold Items Total Price": "",
      "Remaining Price": totalRemaining,
    });

    const worksheet = XLSX.utils.json_to_sheet(bodyData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory");

    const maxWidth = 20;
    worksheet["!cols"] = exportHeaders.map(() => ({ wch: maxWidth }));

    XLSX.writeFile(workbook, "inventory_report.xlsx");
  };

  return (
    <div style={{ width: "98%", margin: "auto" }}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />

      <div>
        <SimpleGrid cols={6}>
          <div>
            <Select
              data={[
                { value: "DAILY", label: "Daily" },
                { value: "WEEKLY", label: "Weekly" },
                { value: "MONTHLY", label: "Monthly" },
                { value: "QUARTERLY", label: "Half Year" },
                { value: "YEARLY", label: "Yearly" },
                { value: "CUSTOM", label: "Custom Date" },
              ]}
              value={timeRange}
              onChange={(value) => {
                setTimeRange(value);
                // if user chooses custom, show controls
                if (value === "CUSTOM") {
                  setShowCustomDates(true);
                } else {
                  setShowCustomDates(false);
                  setSelectedStartDate(null);
                  setSelectedEndDate(null);
                }
              }}
              label="Select Period"
              placeholder="Select Range"
              withinPortal
              clearable
            />
          </div>
          {/* custom date cell(s) */}
          {showCustomDates ? (
            <>
              <div>
                <DatePicker
                  value={selectedStartDate}
                  onChange={setSelectedStartDate}
                  placeholder="Pick a date"
                  label="Select Start Date"
                  clearable
                />
              </div>
              <div>
                <DatePicker
                  value={selectedEndDate}
                  onChange={setSelectedEndDate}
                  placeholder="Pick a date"
                  label="Select End Date"
                  clearable
                />
              </div>
            </>
          ) : (
            <div>
              <Button
                variant="outline"
                onClick={() => setShowCustomDates(true)}
                style={{ width: "100%", marginTop: "22px" }}
              >
                Custom Date
              </Button>
            </div>
          )}
          <div>
            <WarehouseFilter
              valueType="id"
              warehouse={warehouse}
              onCardClick={setSetWarehouse}
            />
          </div>
        </SimpleGrid>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
            gap: "10px",
          }}
        >
          <Menu shadow="md" trigger="hover" openDelay={100} closeDelay={400}>
            <Menu.Target>
              <Button
                style={{
                  width: "80px",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
              >
                Export
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item onClick={exportToPDF}>PDF</Menu.Item>
              <Menu.Item onClick={exportToExcel}>Excel</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </div>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            data={data ? data.getProductsWithStockLevels?.data : []}
            size={size}
            searchInput={false}
            handlePageSizeChange={handlePageSizeChange}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              flexDirection: "column",
              p: 1,
              gap: 1,
              m: 1,
              bgcolor: "background.paper",
              borderRadius: 1,
            }}
          >
            <div
              style={{
                marginRight: "35px",
                fontWeight: "bold",
                fontSize: "15px",
              }}
            >
              Total Sold Price:
              {formatNumber(
                data?.getProductsWithStockLevels?.total_sold_amount,
              )}
            </div>
            <div
              style={{
                marginRight: "35px",
                fontWeight: "bold",
                fontSize: "15px",
              }}
            >
              Total Remaining Price:
              {formatNumber(
                data?.getProductsWithStockLevels?.total_remaining_balance,
              )}
            </div>
          </Box>
        </ScrollArea>
      </Card>
    </div>
  );
};

export default InventoryReport;
